/**
 * MCPStatusPanel Component
 *
 * Displays real-time MCP connection status for all servers
 * Shows connection state, health, retry progress, and errors
 */

import React from 'react';
import { useMCPStatus } from '../hooks';
import type { MCPConnectionStatus, MCPConnectionState } from '../../../lib/api/tools';
import { useI18n } from '@/i18n';

// State color mapping
const stateColors: Record<MCPConnectionState, { bg: string; text: string; dot: string }> = {
  connected: { bg: 'bg-green-50', text: 'text-green-700', dot: 'bg-green-500' },
  connecting: { bg: 'bg-blue-50', text: 'text-blue-700', dot: 'bg-blue-500' },
  reconnecting: { bg: 'bg-yellow-50', text: 'text-yellow-700', dot: 'bg-yellow-500' },
  disconnected: { bg: 'bg-gray-50', text: 'text-gray-700', dot: 'bg-gray-400' },
  failed: { bg: 'bg-red-50', text: 'text-red-700', dot: 'bg-red-500' },
};

// Health badge colors
const healthColors: Record<string, string> = {
  healthy: 'bg-green-100 text-green-800',
  unhealthy: 'bg-red-100 text-red-800',
  unknown: 'bg-gray-100 text-gray-800',
};

interface MCPStatusPanelTranslations {
  title?: string;
  type?: string;
  transport?: string;
  tools?: string;
  uptime?: string;
  retryProgress?: string;
  lastError?: string;
  nextRetry?: string;
  loading?: string;
  loadFailed?: string;
  retry?: string;
  refreshStatus?: string;
  reconnectAll?: string;
  reconnecting?: string;
  autoRefreshing?: string;
  totalServers?: string;
  connected?: string;
  reconnectingCount?: string;
  failed?: string;
  allHealthy?: string;
  degraded?: string;
  unhealthy?: string;
}

interface ServerCardProps {
  server: MCPConnectionStatus;
  t?: MCPStatusPanelTranslations;
}

const ServerCard: React.FC<ServerCardProps> = ({ server, t }) => {
  const colors = stateColors[server.state] || stateColors.disconnected;
  const healthColor = healthColors[server.health] || healthColors.unknown;

  // Format uptime
  const formatUptime = (seconds: number | null): string => {
    if (seconds === null) return '-';
    if (seconds < 60) return `${Math.round(seconds)}s`;
    if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
    return `${Math.round(seconds / 3600)}h ${Math.round((seconds % 3600) / 60)}m`;
  };

  // Format time ago
  const formatTimeAgo = (isoString: string | null): string => {
    if (!isoString) return '-';
    const date = new Date(isoString);
    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    return `${Math.floor(seconds / 3600)}h ago`;
  };

  return (
    <div className={`rounded-lg border p-4 ${colors.bg}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className={`w-2.5 h-2.5 rounded-full ${colors.dot} animate-pulse`} />
          <h4 className="font-semibold text-gray-900">{server.server_id}</h4>
        </div>
        <div className="flex items-center gap-2">
          <span className={`px-2 py-0.5 rounded text-xs font-medium ${healthColor}`}>
            {server.health}
          </span>
          <span className={`px-2 py-0.5 rounded text-xs font-medium ${colors.bg} ${colors.text} border`}>
            {server.state}
          </span>
        </div>
      </div>

      {/* Info Grid */}
      <div className="grid grid-cols-2 gap-2 text-sm">
        <div className="flex justify-between">
          <span className="text-gray-500">{t?.type || 'Type'}:</span>
          <span className="font-medium">{server.server_type}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-500">{t?.transport || 'Transport'}:</span>
          <span className="font-medium">{server.transport}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-500">{t?.tools || 'Tools'}:</span>
          <span className="font-medium">{server.tool_count}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-500">{t?.uptime || 'Uptime'}:</span>
          <span className="font-medium">{formatUptime(server.uptime_seconds)}</span>
        </div>
      </div>

      {/* Retry Progress */}
      {(server.state === 'reconnecting' || server.state === 'connecting') && (
        <div className="mt-3 p-2 bg-white/50 rounded">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">{t?.retryProgress || 'Retry Progress'}:</span>
            <span className="font-medium">
              {server.retry_count} / {server.max_retries}
            </span>
          </div>
          {/* Progress bar */}
          <div className="mt-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-yellow-500 transition-all duration-300"
              style={{ width: `${(server.retry_count / server.max_retries) * 100}%` }}
            />
          </div>
          {server.next_retry_at && (
            <div className="mt-1 text-xs text-gray-500">
              {t?.nextRetry || 'Next retry'}: {formatTimeAgo(server.next_retry_at)}
            </div>
          )}
        </div>
      )}

      {/* Error Display */}
      {server.last_error && (
        <div className="mt-3 p-2 bg-red-100/50 rounded border border-red-200">
          <div className="text-xs text-red-700 font-medium">{t?.lastError || 'Last Error'}:</div>
          <div className="text-sm text-red-600 truncate" title={server.last_error}>
            {server.last_error}
          </div>
          {server.last_error_at && (
            <div className="text-xs text-red-500 mt-1">
              {formatTimeAgo(server.last_error_at)}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export interface MCPStatusPanelProps {
  autoRefresh?: boolean;
  compact?: boolean;
}

export const MCPStatusPanel: React.FC<MCPStatusPanelProps> = ({
  autoRefresh = true,
  compact = false,
}) => {
  const { messages } = useI18n();
  const t = messages.systemSettings.mcp?.statusPanel;
  const { data, isLoading, error, refresh, refreshConnections } = useMCPStatus(autoRefresh);
  const [isRefreshing, setIsRefreshing] = React.useState(false);

  const handleRefreshConnections = async () => {
    setIsRefreshing(true);
    try {
      await refreshConnections();
    } finally {
      setIsRefreshing(false);
    }
  };

  if (isLoading && !data) {
    return (
      <div className="p-4 text-center text-gray-500">
        <div className="inline-block w-6 h-6 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin" />
        <span className="ml-2">{t?.loading || 'Loading MCP status...'}</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
        <div className="text-red-700 font-medium">{t?.loadFailed || 'Failed to load MCP status'}</div>
        <div className="text-sm text-red-600">{String(error)}</div>
        <button
          onClick={() => refresh()}
          className="mt-2 px-3 py-1 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200"
        >
          {t?.retry || 'Retry'}
        </button>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="space-y-4">
      {/* Header with summary */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h3 className="text-lg font-semibold text-gray-900">{t?.title || 'MCP Connection Status'}</h3>
          {/* Pool health indicator */}
          <div className="flex items-center gap-2">
            {data.pool_healthy ? (
              <span className="flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded text-sm">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                {t?.allHealthy || 'All Healthy'}
              </span>
            ) : data.degraded ? (
              <span className="flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-700 rounded text-sm">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                {t?.degraded || 'Degraded'}
              </span>
            ) : (
              <span className="flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded text-sm">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                {t?.unhealthy || 'Unhealthy'}
              </span>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => refresh()}
            disabled={isLoading}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
            title={t?.refreshStatus || 'Refresh status'}
          >
            <svg
              className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
          <button
            onClick={handleRefreshConnections}
            disabled={isRefreshing}
            className="px-3 py-1.5 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isRefreshing ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                {t?.reconnecting || 'Reconnecting...'}
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                {t?.reconnectAll || 'Reconnect All'}
              </>
            )}
          </button>
        </div>
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-4 gap-4">
        <div className="p-3 bg-gray-50 rounded-lg text-center">
          <div className="text-2xl font-bold text-gray-900">{data.total_servers}</div>
          <div className="text-sm text-gray-500">{t?.totalServers || 'Total Servers'}</div>
        </div>
        <div className="p-3 bg-green-50 rounded-lg text-center">
          <div className="text-2xl font-bold text-green-600">{data.connected_count}</div>
          <div className="text-sm text-green-600">{t?.connected || 'Connected'}</div>
        </div>
        <div className="p-3 bg-yellow-50 rounded-lg text-center">
          <div className="text-2xl font-bold text-yellow-600">{data.reconnecting_count}</div>
          <div className="text-sm text-yellow-600">{t?.reconnectingCount || 'Reconnecting'}</div>
        </div>
        <div className="p-3 bg-red-50 rounded-lg text-center">
          <div className="text-2xl font-bold text-red-600">{data.failed_count}</div>
          <div className="text-sm text-red-600">{t?.failed || 'Failed'}</div>
        </div>
      </div>

      {/* Server cards */}
      {!compact && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.servers.map((server) => (
            <ServerCard key={server.server_id} server={server} t={t} />
          ))}
        </div>
      )}

      {/* Compact list view */}
      {compact && (
        <div className="space-y-2">
          {data.servers.map((server) => {
            const colors = stateColors[server.state] || stateColors.disconnected;
            return (
              <div
                key={server.server_id}
                className={`flex items-center justify-between p-2 rounded ${colors.bg}`}
              >
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${colors.dot}`} />
                  <span className="font-medium">{server.server_id}</span>
                  <span className="text-xs text-gray-500">({server.transport})</span>
                </div>
                <div className="flex items-center gap-2">
                  {server.last_error && (
                    <span className="text-xs text-red-600 truncate max-w-[150px]" title={server.last_error}>
                      {server.last_error}
                    </span>
                  )}
                  <span className={`px-2 py-0.5 rounded text-xs ${colors.text}`}>
                    {server.state}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Auto-refresh indicator */}
      {autoRefresh && (
        <div className="text-xs text-gray-400 text-right">
          {t?.autoRefreshing || 'Auto-refreshing every 10s'}
        </div>
      )}
    </div>
  );
};
